// Combo's drill-down: which level of the nested answer every surface is showing.
//
// It is a report param like the rest of the block's state, so it opens on the stored one and is
// announced back for the shell to write down. It is also walked against the answer rather than
// held independently of it, so a payload that no longer carries a level truncates it - which is
// why a new payload re-parses instead of keeping what was on screen.

import { isEqual } from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  parseScope,
  resolveAnswer,
  resolveAnswerVariants,
  serializeScope
} from '@aytm/app/stats/page/question-views/cx/lib/scope'

export default function useComboScope({ param, answer, answerVariants, onScopeChange } = {}) {
  const [scope, setScope] = useState(() => parseScope(param ?? null, answer))

  const inputs = useRef()
  inputs.current = { scope, answer, answerVariants }

  const onScopeChangeRef = useRef(onScopeChange)
  onScopeChangeRef.current = onScopeChange

  // the stored level moved outside the block, or a payload arrived to walk it against
  const seeded = useRef({ param, answer })
  useEffect(() => {
    if (seeded.current.param === param && seeded.current.answer === answer) return
    seeded.current = { param, answer }

    const parsed = parseScope(param ?? null, answer)
    setScope(current => (isEqual(current, parsed) ? current : parsed))
  }, [param, answer])

  const moveTo = useCallback(next => {
    if (isEqual(next, inputs.current.scope)) return

    setScope(next)
    onScopeChangeRef.current?.(serializeScope(next))
  }, [])

  // descend one level, when the clicked variant has anything to descend into
  const drillTo = useCallback(
    position => {
      if (!position) return

      const { scope, answer, answerVariants } = inputs.current

      const current = resolveAnswer(scope, answer)
      const variant = (resolveAnswerVariants(scope, answerVariants) || []).find(av => av?.position === position)

      // a leaf with no corpus of its own is as deep as the drill-down goes
      if (!variant?.children && null == current?.[`a${position}`]?.cloudWords) return

      const next = scope.slice()
      next.splice(-1, 0, position)

      moveTo(next)
    },
    [moveTo]
  )

  const handlers = useMemo(
    () => ({
      onScopeChange: moveTo,
      // A combo word is an answer variant, not a corpus term, so clicking one descends a level
      // instead of entering active-word mode -- the graph the active word drives has nothing to
      // work with here, since the position slot of a combo word triple holds the variant. The
      // cloud's own breadcrumb hands back a whole scope; a word hands back the level to add.
      onWordClick: ({ scope, word }) => (null != scope ? moveTo(scope) : drillTo(word?.id)),
      // cx rows are the drill-down level itself, so the label descends into the clicked variant
      // instead of applying a filter
      onScopeLabelClick: (event, position) => {
        event?.stopPropagation?.()

        drillTo(position)
      }
    }),
    [moveTo, drillTo]
  )

  const getScope = useCallback(() => inputs.current.scope, [])

  return { scope, handlers, getScope }
}
